// @workflow assistant.clarification
/**
 * The safety net for questions the model writes as text (contract "Clarifications: ask, don't
 * guess"). The server asks through `ask_user`, and the panel shows it; but a model can still end a
 * reply with a question and a list of choices in plain text (seen live 2026-10-14: the second of two
 * follow-up questions). Every follow-up must look and work the same, so such a reply is shown in the
 * same clarification panel. Its answer goes back as a normal message in the same conversation (the
 * model reads it as the user's reply); there is no server request to resolve.
 *
 * Deliberately narrow: only the end of the last finished reply, only a line ending in "?" followed
 * by 2–4 list items and nothing after them. Anything else stays text.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { ROLE, MESSAGE_STATUS, PART } from './events.js';
import { CLARIFICATION_LIMITS, cleanClarification } from './clarification.js';

/** Marks a clarification built from reply text (it has no server request behind it). */
export const TEXT_QUESTION_SOURCE = 'text';

const LIST_ITEM = /^\s*(?:[-*•]|\d+[.)])\s+(.+?)\s*$/;
const ENDS_WITH_QUESTION = /\?\s*$/;
/** "Label (details)." → label + details; "Label: details" and "Label — details" likewise. */
const PAREN_TAIL = /^(.+?)\s*\((.+)\)\s*\.?$/;
const SEPARATOR = /^(.+?)\s*(?::|\s[—–-]\s)\s*(.+)$/;
const TRAILING_DOT = /\.\s*$/;
/** A marker that only numbers a choice: one letter or a number ("A", "b", "1", "12"). */
const BARE_MARKER = /^(?:[A-Za-z]|\d{1,2})$/;
/** "A) Run…", "(b) Run…", "C. Run…": a marker before the choice's own words. */
const LEADING_MARKER = /^\(?(?:[A-Za-z]|\d{1,2})[.)]\s+(.+)$/;
const EDGE_PUNCTUATION = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;
/** The closing bold or italic of a marker ("**Option A:** Run…") left at the start of the meaning. */
const LEADING_EMPHASIS = /^[*_]+\s*/;
const WORDS = /\s+/;
const FIRST_LETTER = 'A'.charCodeAt(0);
/** Joins a numbered head and its meaning in a label: "Train 1 · Inspect the separator". */
const HEAD_JOIN = ' · ';
const ELLIPSIS = '…';
const ID_PREFIX = 'choice-';

const lastTextPartOf = (message) => {
  const texts = (message.parts || []).filter((p) => p.type === PART.TEXT);
  return texts.length ? texts[texts.length - 1] : null;
};

/** A short label that fits the panel: cut at a word, never mid-word. */
function shortLabel(text) {
  if (text.length <= CLARIFICATION_LIMITS.LABEL_LENGTH) return text;
  const room = CLARIFICATION_LIMITS.LABEL_LENGTH - ELLIPSIS.length;
  const cut = text.slice(0, room);
  const space = cut.lastIndexOf(' ');
  return `${(space > 0 ? cut.slice(0, space) : cut).trim()}${ELLIPSIS}`;
}

/** The marker of the choice at `index` (0-based): its letter ("A", "b") or its number ("1"). */
const isPosition = (word, index) => word.toUpperCase() === String.fromCharCode(FIRST_LETTER + index) || word === String(index + 1);

/** The head's words without the punctuation around them ("**Option A" → ["Option", "A"]). */
const wordsOf = (head) => head.replace(EDGE_PUNCTUATION, '').split(WORDS).filter(Boolean);

/**
 * A choice as its label and details, told by the head's shape, never by a list of words:
 * - a bare letter or number ("A)", "(b)", "2") only numbers the choice: the label is the meaning;
 * - one word and the choice's own position ("Option A" first, "Train 2" second) may be a real
 *   name, so it is never dropped: the label is "<head> · <meaning>", the details the whole choice;
 * - any other head ("Phase 3" first, "Findings") is the label, the rest its details.
 */
function splitChoice(text, index) {
  const led = text.match(LEADING_MARKER);
  const body = led ? led[1] : text;
  const split = body.match(PAREN_TAIL) || body.match(SEPARATOR);
  if (!split) return { label: body, description: '' };
  const words = wordsOf(split[1]);
  const meaning = split[2].replace(LEADING_EMPHASIS, '').trim();
  if (words.length === 1 && BARE_MARKER.test(words[0])) return splitChoice(meaning, index);
  if (words.length === 2 && isPosition(words[1], index)) return { label: `${words.join(' ')}${HEAD_JOIN}${meaning}`, description: text };
  return { label: split[1].trim(), description: split[2].trim() };
}

function optionOf(item, index) {
  const original = item.replace(TRAILING_DOT, '').trim();
  let { label, description } = splitChoice(original, index);
  if (label.length > CLARIFICATION_LIMITS.LABEL_LENGTH) {
    description = description ? original : label;
    label = shortLabel(label);
  }
  const option = { id: `${ID_PREFIX}${index + 1}`, label };
  if (description) option.description = description.slice(0, CLARIFICATION_LIMITS.DESCRIPTION_LENGTH);
  return { option, original: item.trim() };
}

/** The question and its choices at the very end of `text`, and the text before them; or null. */
function trailingQuestion(text) {
  const raw = text.split('\n');
  const lines = raw.map((l) => l.trimEnd());
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  const items = [];
  while (lines.length && LIST_ITEM.test(lines[lines.length - 1])) items.unshift(lines.pop().match(LIST_ITEM)[1]);
  while (lines.length && !lines[lines.length - 1].trim()) lines.pop();
  const question = lines.length ? lines[lines.length - 1].trim() : '';
  if (!ENDS_WITH_QUESTION.test(question)) return null;
  if (items.length < CLARIFICATION_LIMITS.MIN_OPTIONS || items.length > CLARIFICATION_LIMITS.MAX_OPTIONS) return null;
  return { question, items, before: raw.slice(0, lines.length - 1).join('\n').trimEnd() };
}

/**
 * The text question of the conversation's last reply, shaped like a clarification part (so the
 * panel and the composer's answering mode work unchanged), or null.
 * @param {{ messages: Array<object> }} conversation
 */
export function textQuestionOf(conversation) {
  const messages = (conversation && conversation.messages) || [];
  const last = messages[messages.length - 1];
  if (!last || last.role !== ROLE.ASSISTANT || last.status !== MESSAGE_STATUS.DONE) return null;
  if ((last.parts || []).some((p) => p.type === PART.CLARIFICATION)) return null;
  const part = lastTextPartOf(last);
  const found = part && trailingQuestion(part.text || '');
  if (!found) return null;
  const built = found.items.map(optionOf);
  const clean = cleanClarification({ question: found.question, options: built.map((b) => b.option), allowFreeText: true, multiSelect: false });
  if (!clean.ok) return null;
  return {
    ...clean.value,
    requestId: `${TEXT_QUESTION_SOURCE}:${last.id}`,
    messageId: last.id,
    source: TEXT_QUESTION_SOURCE,
    originals: Object.fromEntries(built.map((b) => [b.option.id, b.original.replace(TRAILING_DOT, '')])),
    /** The text part the question was read from, and its text before the question. */
    partId: part.id,
    textBefore: found.before,
  };
}

/**
 * The text question the panel shows: the last reply's, unless the user closed its panel.
 * @param {{ messages: Array<object> }} conversation
 * @param {string|null} closedRequestId the requestId of the text question the user closed
 */
export function openTextQuestion(conversation, closedRequestId) {
  const question = textQuestionOf(conversation);
  return question && question.requestId !== closedRequestId ? question : null;
}

/**
 * What a reply's text part shows: while the panel shows its question, the text before the
 * question ('' when the question was all of it), so the question and its choices are not shown
 * twice. The stored text is never changed: Copy, history and the answered thread keep all of it.
 * @param {string} messageId the reply the part belongs to (part ids come from the server and may repeat across replies)
 * @param {{ id: string, text: string }} part a text part
 * @param {object|null} shown the text question on screen (openTextQuestion), or null
 */
export function shownReplyText(messageId, part, shown) {
  return shown && shown.messageId === messageId && shown.partId === part.id ? shown.textBefore : part.text;
}

/**
 * What the user sends for a text question: each chosen choice in full, then what they typed.
 * @param {{ originals: Record<string, string> }} question
 * @param {{ optionIds?: string[], text?: string }} answer
 * @returns {string} '' when nothing was chosen or typed
 */
export function textAnswerMessage(question, { optionIds = [], text = '' } = {}) {
  const chosen = optionIds.map((id) => question.originals[id]).filter(Boolean).join('; ');
  const typed = (text || '').trim();
  if (chosen && typed) return `${chosen}. ${typed}`;
  return chosen || typed;
}
